import { usePagination, useActions, type Page } from "@nosto/search-js/preact/hooks"
import Icon from "@/elements/Icon/Icon"
import { JSX } from "preact/jsx-runtime"
import style from "./Pagination.module.css"
import { getPageUrl } from "@/mapping/url/getPageUrl"
import { cl } from "@nosto/search-js/utils"
import { useStyleFamilyCatalog } from "@/contexts/StyleFamilyCatalogContext"
import { useDedupedPagination } from "@/hooks/useDedupedPagination"
import { useNostoAppState } from "@nosto/search-js/preact/hooks"
import { defaultSize, styleFamilyCatalogMaxRawTotal } from "@/config"
import { useMemo } from "preact/hooks"
import { readInGridConfig } from "@/utils/ingridConfig"
import { computeInGridSlotCount } from "@/utils/paginationWithInGrid"

type Props = {
  ariaLabel?: string
} & JSX.IntrinsicElements["a"]

/**
 * Scroll to the top of the page after a pagination click. Blur first because
 * Safari/Firefox restore scroll to the focused element on `history.pushState`,
 * which on the last page (shorter document) lands the user at the bottom.
 * Defer the scroll until after the next paint so it survives any layout shift
 * triggered by the upcoming state update.
 */
function scrollToTop(focused: HTMLElement | null) {
  focused?.blur()
  if (typeof window === "undefined") return
  if (typeof window.history?.scrollRestoration === "string") {
    window.history.scrollRestoration = "manual"
  }
  window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior })
  requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior })
  })
}

function PageLink({ onClick, href, className, ariaLabel, children }: Props) {
  return (
    <a className={cl(style.link, className)} href={href} aria-label={ariaLabel} onClick={onClick}>
      {children}
    </a>
  )
}

function PaginationLinks({
  prev,
  first,
  pages,
  last,
  next,
  resolvePageClick
}: {
  prev?: Page
  first?: Page
  pages: Page[]
  last?: Page
  next?: Page
  resolvePageClick: (page: Page) => { href: string; onClick: (e: Event) => void }
}) {
  return (
    <ul className={style.container}>
      {prev && (
        <li>
          <PageLink {...resolvePageClick(prev)} ariaLabel="Previous page">
            <Icon name="arrow-left" />
          </PageLink>
        </li>
      )}

      {first && (
        <>
          <li>
            <PageLink {...resolvePageClick(first)} ariaLabel="First page">
              {first.page}
            </PageLink>
          </li>
          <li>
            <span className={style.filler}>...</span>
          </li>
        </>
      )}

      {pages.map(page => (
        <li key={page.page} className={cl(page.current && style.active)}>
          <PageLink ariaLabel={`${page.page} page`} {...resolvePageClick(page)}>
            {page.page}
          </PageLink>
        </li>
      ))}

      {last && (
        <>
          <li>
            <span className={style.filler}>...</span>
          </li>
          <li>
            <PageLink {...resolvePageClick(last)} ariaLabel="Last page">
              {last.page}
            </PageLink>
          </li>
        </>
      )}

      {next && (
        <li>
          <PageLink {...resolvePageClick(next)} ariaLabel="Next page">
            <Icon name="arrow-right" />
          </PageLink>
        </li>
      )}
    </ul>
  )
}

function NostoPagination() {
  const { prev, first, pages, last, next } = usePagination({
    width: 5
  })

  const { updateSearch } = useActions()

  function resolvePageClick({ from, page }: Page) {
    return {
      href: getPageUrl(page),
      onClick: (e: Event) => {
        e.preventDefault()
        scrollToTop(e.currentTarget as HTMLElement | null)
        updateSearch({ products: { from } })
      }
    }
  }

  return (
    <PaginationLinks
      prev={prev}
      first={first}
      pages={pages}
      last={last}
      next={next}
      resolvePageClick={resolvePageClick}
    />
  )
}

function DedupedPagination() {
  const catalog = useStyleFamilyCatalog()!
  const pageSize = useNostoAppState(s => s.query?.products?.size ?? defaultSize)
  const inGridSlots = useMemo(() => computeInGridSlotCount(readInGridConfig()), [])
  const { prev, first, pages, last, next } = useDedupedPagination({
    width: 5,
    dedupedTotal: catalog.dedupedHits?.length ?? 0,
    pageSize,
    currentPage: catalog.urlPage,
    inGridSlots
  })

  function resolvePageClick({ page }: Page) {
    return {
      href: getPageUrl(page),
      onClick: (e: Event) => {
        e.preventDefault()
        scrollToTop(e.currentTarget as HTMLElement | null)
        catalog.goToPage(page)
      }
    }
  }

  return (
    <PaginationLinks
      prev={prev}
      first={first}
      pages={pages}
      last={last}
      next={next}
      resolvePageClick={resolvePageClick}
    />
  )
}

export default function Pagination() {
  const catalog = useStyleFamilyCatalog()
  const catalogPriming =
    catalog != null &&
    catalog.rawTotal > 0 &&
    catalog.rawTotal <= styleFamilyCatalogMaxRawTotal &&
    (catalog.status === "idle" || catalog.status === "loading")

  if (catalogPriming) {
    return null
  }

  if (catalog?.status === "ready" && catalog.dedupedHits && catalog.dedupedHits.length > 0) {
    return <DedupedPagination />
  }

  return <NostoPagination />
}
